import { Slot } from "@radix-ui/react-slot";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "@/shared/lib/variants";

/**
 * Button primitive. Per design.md §10, every variant applies
 * `rounded-full`, `type-label-base`, and `tracking-wider uppercase`;
 * these are part of the button identity and MUST NOT be overridden.
 *
 * Variants and sizes match the spec exactly:
 *   variant: default | destructive | outline | secondary | ghost | ghost-danger | link
 *   size:    default | sm | lg | icon
 */
const button = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-full type-label-base tracking-wider uppercase",
    "transition-colors duration-150",
    "disabled:opacity-50 disabled:pointer-events-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
  ],
  {
    variants: {
      variant: {
        default: "btn-glass-brand hover:brightness-105 active:brightness-95",
        destructive: "btn-glass-danger hover:brightness-105 active:brightness-95",
        outline:
          "bg-transparent border border-border text-text hover:bg-surface-muted/40",
        secondary: "glass-surface-subtle text-text hover:bg-white/70",
        ghost: "bg-transparent text-text hover:bg-surface-muted/40",
        // The transparent rest border keeps `border-color` animating from
        // transparent → the subtle red on hover. Without it the border falls
        // back to `currentColor` (danger-strong), so the 1px hover border
        // flashes dark red before settling to the light tint.
        "ghost-danger":
          "bg-transparent border border-transparent text-danger-strong hover:btn-glass-danger-subtle",
        link: "bg-transparent text-brand underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-6",
        sm: "h-8 px-4",
        lg: "h-12 px-8",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return <Comp ref={ref} className={cn(button({ variant, size }), className)} {...props} />;
});
